export interface IRetryParams {
  retryMs: number | number[],
  maxAttempts?: number,
  failAfterLastTimeout?: boolean,
}

export function isRetryParams(params: any): params is IRetryParams {
  return params?.retryMs !== undefined
    || params.maxAttempts !== undefined
    || params.failAfterLastTimeout !== undefined;
}

/**
 * Wraps a given function with logic that will call the function and wait for 1 of 3 things to happen:
 * 1) a successful result, at which point the result will be returned immediately
 * 2) an error, after which it will try again
 * 3) some number of milliseconds to elapse, after which it will start another attempt concurrently
 *
 * @param options.retryMs Timeout in milliseconds before starting another attempt.  If an array is given, each entry in the
 *                  array corresponds to the timeout for that attempt. e.g [100, 500, 1000] will wait 100ms for the
 *                  first attempt, 500ms for the second attempt, and 1s for the third attempt.
 *
 * @param options.maxAttempts Optional. Maximum number of attempts to try. Defaults to the length of timeoutMs if it is an
 *                    array, otherwise defaults to 2.
 *
 * @param options.failAfterLastTimeout If set to true, an error will be generated after all attempts go past the timeout.
 *                             I.e. do not wait for all attempts to finish if they've all already gone past the timeout.
 */
export async function retryOnFail<T>(
  fn: () => Promise<T>,
  options: IRetryParams)
: Promise<T> {
  const { retryMs: timeoutMs, failAfterLastTimeout } = options;
  let { maxAttempts } = options;
  if (!maxAttempts) {
    const maxAttemptsDefault = 2;
    maxAttempts = typeof timeoutMs === 'object' && timeoutMs.length || maxAttemptsDefault;
  }

  function getTimeoutMs(iAttempt: number) {
    if (typeof timeoutMs === 'number') {
      return timeoutMs;
    }
    if (iAttempt >= timeoutMs.length) {
      return timeoutMs[timeoutMs.length - 1];
    }
    return timeoutMs[iAttempt];
  }

  return new Promise(async (resolve, reject) => {
    let pendingAttempts = 0;
    let done = false;

    for (let iAttempt = 0; iAttempt < maxAttempts; iAttempt++) {
      if (done) {
        return;
      }
      let timeoutId;
      let resolveTimeout;
      const timeout = new Promise<undefined>(_resolveTimeout => {
        resolveTimeout = _resolveTimeout;
        timeoutId = setTimeout(resolveTimeout, getTimeoutMs(iAttempt));
      });

      pendingAttempts++;
      const attempt = fn()
        .then(result => {
          pendingAttempts--;
          if (!done) {
            done = true;
            resolve(result);
          }
        })
        .catch(err => {
          pendingAttempts--;
          if (!done && pendingAttempts === 0 && iAttempt === (maxAttempts - 1)) {
            done = true;
            reject(err);
          }
        });

      await Promise.race([ attempt, timeout ]);
      clearTimeout(timeoutId);
      resolveTimeout();
    }
    if (!done && failAfterLastTimeout) {
      done = true;
      reject(new Error('No response received within allotted time'));
    }
  })
}
